
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  const openNativeApp = async () => {
    if (isNative) {
      try {
        console.log('ResetPassword: Opening native app...');
        await Browser.open({
          url: 'mindnote://home',
          windowName: '_self'
        });
      } catch (error) {
        console.error('ResetPassword: Failed to open native app:', error);
      }
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('ResetPassword: Checking for reset tokens');
        console.log('ResetPassword: Current URL:', window.location.href);
        console.log('ResetPassword: Hash:', window.location.hash);
        console.log('ResetPassword: Search:', window.location.search);

        // First try to parse the hash parameters
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        const type = hashParams.get('type') || searchParams.get('type');

        console.log('ResetPassword: Found tokens:', { 
          accessToken: !!accessToken, 
          refreshToken: !!refreshToken, 
          type 
        });

        // If we have tokens in the URL, try to set the session
        if (accessToken && refreshToken) {
          console.log('ResetPassword: Setting session with tokens');
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('ResetPassword: Error setting session:', error);
            toast({
              title: "Session Error",
              description: "Unable to validate your reset token. Please request a new password reset link.",
              variant: "destructive",
            });
            navigate('/auth');
            return;
          }
          
          console.log('ResetPassword: Session set successfully');
          return; // Valid session set, allow password reset
        }

        // If no hash params, check for an active session
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('ResetPassword: Current session check:', { hasSession: !!session, error });
        
        if (error || !session) {
          console.log('ResetPassword: No valid session found');
          toast({
            title: "Invalid reset link",
            description: "Please request a new password reset link.",
            variant: "destructive",
          });
          navigate('/auth');
          return;
        }
      } catch (error) {
        console.error('ResetPassword: Session check error:', error);
        toast({
          title: "Session Error",
          description: "Please request a new password reset link.",
          variant: "destructive",
        });
        navigate('/auth');
      }
    };

    checkSession();
  }, [navigate, toast]);

  const validatePassword = (password: string): { isValid: boolean; message: string } => {
    if (password.length < 6) {
      return { isValid: false, message: "Password must be at least 6 characters long" };
    }
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, message: "Password must contain at least one uppercase letter" };
    }
    if (!/[a-z]/.test(password)) {
      return { isValid: false, message: "Password must contain at least one lowercase letter" };
    }
    if (!/[0-9]/.test(password)) {
      return { isValid: false, message: "Password must contain at least one number" };
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return { isValid: false, message: "Password must contain at least one special character" };
    }
    return { isValid: true, message: "" };
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast({
        title: "Invalid password",
        description: validation.message,
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords match",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log('ResetPassword: Updating password');
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('ResetPassword: Password update error:', error);
        throw error;
      }

      console.log('ResetPassword: Password updated successfully');
      toast({
        title: "Password updated successfully",
        description: "You can now sign in with your new password",
      });
      
      await supabase.auth.signOut();
      
      // Open native app if on mobile, otherwise navigate to auth
      if (isNative) {
        setTimeout(() => {
          openNativeApp();
        }, 2000);
      } else {
        navigate('/auth');
      }
    } catch (error: any) {
      console.error('ResetPassword: Error updating password:', error);
      toast({
        title: "Error updating password",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="p-4 sm:p-6">
          <h2 className="text-2xl font-bold mb-6 text-center">Reset Password</h2>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
                className="w-full"
                placeholder="Min. 6 characters with upper, lower, number, symbol"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                className="w-full"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Updating Password..." : "Update Password"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
